import styled, {css} from "styled-components";
import {brand, createCard, fontFamilies, radii} from "../theme/tokens";
import {CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX} from "./landing/campaignSpace";
import {FullWidthPanelButton, SubmitButton} from "./components/TransferPanel.styles";
import {surfaceHeight} from "../nav/SiteFooter.styles";

/**
 * The breathing space the flow keeps above and below the card, and the one length the card's own
 * viewport cap borrows: a hanging peel tag needs its paper AND this gap to stay on screen, because the
 * column is laid out inside this padding rather than against the viewport's edge.
 */
const BACKDROP_BLOCK_PADDING = "32px";

export const FlowRoot = styled.div`
	width: 100%;
	height: 100%;
`;

export const Backdrop = styled.div<{$success?: boolean}>`
	min-height: 100%;
	width: 100%;
	box-sizing: border-box;
	display: grid;
	/* A definite track keeps the fixed-width card constrained by narrow parent containers. */
	grid-template-columns: minmax(0, 1fr);
	place-items: center;
	align-content: center;
	/* No overflow rule on purpose (ui#2022): the column around the card is exactly card-width, and
	   any clip here lands ON the card's edge — 'overflow-y: auto' forces horizontal clipping too,
	   which cut the side shadow off (invisible against the old gradient ground, plain on the flat
	   one). Vertical overflow belongs to the page's own scroll container ('.stage', the documented
	   escape hatch); the card's one internal scroll region is the file list, not this wrapper. */
	padding: ${BACKDROP_BLOCK_PADDING} 0;
	/* Success sits 28px higher on desktop (owner 2026-09-12): asymmetric padding shifts the grid's
	   centring, so nothing gains a transform (a transform here would become the containing block
	   for any fixed descendant). Mobile keeps the centred rest position. */
	${({$success}) => $success && css`
		@media (min-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX}px) { padding: 4px 0 60px; }
	`}
`;

/**
 * The create card's fixed measure (design `.card`), as CSS.
 *
 * Named because three places on this page have to agree on it: this card, the column that reserves its
 * width on desktop ({@link ../HomePage.styles.FlowColumn}) and — since ui#1106 — the editorial block
 * that sits under the card below 901px and has to share its left edge.
 *
 * The NUMBER moved to `theme/tokens.createCard` in kitecorp/transferify-ui#1922, when the recipient
 * page's payload box became the same measure in the same place. It is read from there rather than
 * declared here so the download route can have the number without importing the sender form's
 * stylesheet to get it; this export stays because the three readers above are about this card, not
 * about the token.
 */
export const CREATE_CARD_WIDTH = `${createCard.widthPx}px`;
/** The column the card sits in beside the campaign, where it is narrower (`createCard.sideBySideWidthPx`). */
export const CREATE_CARD_SIDE_BY_SIDE_WIDTH = `${createCard.sideBySideWidthPx}px`;

/**
 * The card's own border width, named because {@link ConsentDock} has to SUBTRACT it to find the
 * card's inner corner — see the radius note there. Two literal `1px`s in two files would be a corner
 * that develops a nick the day anyone thickens this edge.
 */
const CARD_BORDER_WIDTH = "1px";

/**
 * The create card's horizontal padding, named for the same reason: {@link ConsentDock} fills the
 * card's PADDING box, so the gate that lands there has to restate this inset itself to keep its text
 * column on the same left edge as the panel's (see `terms/Consent.styles.Root`).
 */
export const CREATE_CARD_INLINE_PADDING = "20px";

/**
 * The create card's vertical padding, named for the same reason and with a measured consequence: the
 * gate's accept control sits exactly one of these above the card's lower edge, and at 320x568 that is
 * what leaves it 23px clear of the undecided cookie banner (measured). Two copies
 * would let someone change the card's padding without moving the gate's, drifting the accept control
 * toward the banner with nothing pointing at the cause.
 */
export const CREATE_CARD_BLOCK_PADDING = "18px";

/**
 * Only the empty drop area uses this inset; selected files and consent keep their existing padding.
 * 20px is the owner's chosen gap between the dashed border and the card edge (2026-09-05, settled
 * on a live localhost review: 8/12/16px read too tight, 24px too much). It pairs with the drop
 * area's 16px radius so the two rounded corners stay visually concentric inside the card's 20px.
 */
export const EMPTY_CREATE_CARD_PADDING = "20px";

/**
 * How far the Request files tab stands above this card's top edge (kitecorp/transferify-ui#3140). It stands
 * in the room the flow column already leaves above the card (its 32px top padding), so the card does not
 * move for it: the landing keeps the geometry its first-screen checks were measured on, with the tab or
 * without it.
 */
export const REQUEST_TAB_HEIGHT_PX = 30;

export const Card = styled.div<{$empty: boolean; $success?: boolean; $composing?: boolean}>`
	/* The positioning context for {@link ConsentDock}, which fills exactly this box's padding box. */
	position: relative;
	width: ${CREATE_CARD_WIDTH};
	max-width: 100%;
	box-sizing: border-box;
	background: ${({theme}) => theme.tokens.card};
	border-radius: ${radii.card};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
	border: ${CARD_BORDER_WIDTH} solid ${({theme}) => theme.tokens.cardBorder};
	/* The success card trims its vertical padding (owner 2026-09-12). Only there: the create
	   phase's {@link CREATE_CARD_BLOCK_PADDING} places the consent gate's accept control, and the
	   success card never hosts that gate. */
	padding: ${({$empty, $success}) => $empty
		? EMPTY_CREATE_CARD_PADDING
		: `${$success ? "12px" : CREATE_CARD_BLOCK_PADDING} ${CREATE_CARD_INLINE_PADDING}`};
	color: ${({theme}) => theme.tokens.ink};
	display: flex;
	flex-direction: column;
	/* The page chrome this card has always kept clear, PLUS whatever hangs under it. The
	   --peel-hanging-reserve property is what the peel frame measures there - the string gap, the
	   paper, and the touch target its action bleeds past its own pill - measured rather than stated,
	   because the paper's height is its copy and the invite tag wraps in Romanian and German where it
	   does not in English (kitecorp/transferify-ui#2485).

	   The card sized as if nothing hung there put the tag's action at the viewport's edge, and a
	   browser asked to scroll a control into view aligns its bottom WITH that edge - so the bleed
	   landed outside and half the control was not there to hit. The whole column has to fit without
	   scrolling for the target to survive, which is why the two reserves add rather than compete: the
	   card gives up the height instead, into its own scroller, on the viewports where they collide.

	   The legal footer row is the third term for the same reason (ui#2631): ui#2394 put the statutory
	   legal row in the app scroller's flow, so the surface this card sits on ends that much above the
	   window and a cap measured to the window's edge overruns it. Measured at 1280x700, where the
	   surface clips what hangs past its end: the gift tag's pull reached 20px past it and Chrome
	   reported a third of that 44px control off screen. The card gives up the height, as it already
	   does for the tag. That term is no longer written out here: nav/SiteFooter.styles owns the one
	   declaration that names the surface instead of the window, so this cap and the three other boxes
	   that need it cannot drift apart (ui#2635). Taking it from there also brought the documented unit
	   with it - this line said 100vh, the LARGE viewport, where ui#687 settled on 100dvh.

	   The reserve is absent wherever nav/siteFooterRoutes keeps the row off, which since ui#2681 is
	   every screen THIS CARD is ever on - the landing, the private destinations it stays mounted behind,
	   and the Personalization sheet - plus the delivery pages it is not on. The fallback then leaves this
	   calc exactly as it was, which is why this needs no route rule of its own. That the term still has
	   to be taken is not vestigial: nothing stops a later route carrying both the row and this card, and
	   the reserve going missing is invisible until a 44px control is half off screen. */
	${surfaceHeight("max-height", "138px", "var(--peel-hanging-reserve, 0px)")}

	/* Below the pinned panel chrome's minimum height, the page owns scrolling instead of nesting it. */
	@media (max-height: 520px) {
		max-height: none;
	}

	/* The same on the stacked phone layout while a selection is composed (kitecorp/transferify-ui#3410). There the
	   fields under the file list (the email, the two mode cards, Options, Personalise, the over-limit strip and
	   Send) can be taller than the cap on their own: at the iPhone 13 profile (390x664) with a signed-out
	   over-limit selection they were, so the file list box was squeezed below its own pinned header and add
	   row, and those spilled under the email field. Uncapped, the card is as tall as what it holds and the page
	   scrolls, as it already does on a phone; a long list is still drawn as a window, against the page. Only
	   while composing: the success card keeps its own scroller under the cap, and the empty drop area is unchanged. */
	${({$composing}) => $composing && css`
		@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) {
			max-height: none;
		}
	`}
`;

export const PanelSlot = styled.div<{$hidden: boolean}>`
	display: ${({$hidden}) => $hidden ? "none" : "contents"};
`;

/**
 * Where the consent gate sits: FILLING the create card, as an opaque takeover (ui#1728).
 *
 * Owner, 2026-08-27: "I don't like that the terms are inline with the content of the drop area. it
 * should be on top of the content and not mess with it's content". So the GATE was the part still in
 * flow: docked in the card's first slot it added 212px to the card, which is what pushed the drop hero
 * down the page whenever the gate was up. Out of flow it adds nothing, and the card below it is the
 * same card either way. That half has not changed since ui#1575 and is what keeps the drop area
 * identical in both gate states — it is the invariant every later ticket, this one included, has had
 * to preserve rather than the arrangement of the moment.
 *
 * <b>A takeover rather than a panel, and both constraints that forced a panel are gone.</b> ui#1575
 * anchored the gate to the ~78px band below the drop hero because covering the upload control was the
 * ui#1549 defect — a visitor arriving on an ad click found a legal notice where the product was
 * supposed to be. ui#1619 removed the capability pitch the card made behind the gate, so there is no
 * longer a claim behind it to read; ui#1713 then centred the gate over the hero on the owner's ask;
 * and ui#1728 takes the last step the approved design asks for — the gate IS the card's content until
 * the visitor agrees, so there is no rim, no floating edge and nothing legible behind it. What ui#1549
 * protects is unchanged and still measured: the control keeps a real box, does not MOVE between the
 * two states, and is operable again the moment the gate is answered.
 *
 * The accept control also sits further from the cookie banner than the ui#1575 bar left it, which is
 * what matters at 320x568 (ui#1584): the banner is a bottom-anchored layer, so a gate whose action
 * ends one card-padding above the card's lower edge keeps clearance at every viewport;
 * `home-hero-editorial.spec.ts` checks the control is operable under the banner at 320x568.
 *
 * LAST in the card rather than first, which is the opposite of ui#1549's arrangement — it matches the
 * reading order to the visual order, so the drop area is announced above the notice.
 */
export const ConsentDock = styled.div`
	position: absolute;
	/* Card is the containing block (it is the only positioned ancestor in the create tree), and an
	   absolute inset resolves against a containing block's PADDING box — so 0 is exactly the card's own
	   surface minus its border. ui#1713's 6px rim is deliberately gone: a rim is what made the gate read
	   as a surface ON the card, and this design wants it read as the card. */
	inset: 0;
	/* The card's INNER corner, which is one border-width tighter than the radii.card arc drawn on its
	   border box. Restating radii.card here would paint the gate's opaque fill over that 1px border at
	   each corner — a visible nick, and a new problem rather than an inherited one: ui#1713's panel was
	   inset far enough that its corner never reached the card's. The gate inherits this value rather
	   than repeating it (see terms/Consent.styles.Root). */
	border-radius: calc(${radii.card} - ${CARD_BORDER_WIDTH});
	/* One cell, stretched: the gate fills this frame, where ui#1713 centred an auto-height row in it.
	   (No backticks in a comment inside a styled-components literal - one would end the literal.) */
	display: grid;
	/* Above the sender panel it covers. The panel is in normal flow and unpositioned, so any positive
	   value clears it; this stays explicit so a future positioned sibling has to argue with a number
	   rather than silently win on document order. */
	z-index: 2;
`;

export const DeliveryRetryRoot = styled.section`
	display: flex;
	flex-direction: column;
	align-items: center;
	min-width: 0;
	padding: 26px 6px 18px;
	font-family: ${fontFamilies.sans};
	text-align: center;
	overflow-wrap: anywhere;
`;

export const DeliveryRetryTitle = styled.h2`
	margin: 0;
	font-size: 19px;
	font-weight: 700;
	line-height: 1.25;
	color: ${({theme}) => theme.tokens.ink};
`;

export const DeliveryRetryCopy = styled.p`
	margin: 8px 0 0;
	max-width: 320px;
	font-size: 13.5px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const DeliveryErrorDetail = styled.p`
	width: 100%;
	min-width: 0;
	box-sizing: border-box;
	margin: 16px 0 0;
	padding: 12px 14px;
	border: 1px solid ${({theme}) => theme.tokens.line};
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	color: ${brand.error};
	font-size: 13px;
	line-height: 1.45;
	overflow-wrap: anywhere;
`;

export const DeliveryRetryActions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
	min-width: 0;
	margin-top: 16px;
`;

export const RetryDeliveryButton = styled(SubmitButton)`
	min-height: 44px;
	margin-top: 0;
`;

export const StartOverButton = styled(FullWidthPanelButton)`
	justify-content: center;
	min-height: 44px;
	margin-top: 0;
`;
